import type { QueryClient } from '@tanstack/react-query';
import type { Project } from '@/api/client';
import { isProjectExcludedFromActiveTable } from '@/utils/projectStatuses';
import { markProjectUpdatedByMutation } from '@/lib/projectRealtimeDedup';
import {
  ARCHIVED_COUNT_QUERY_KEY,
  PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY,
  getProjectEntityFromCaches,
  projectEntityQueryKey,
  setProjectEntity,
} from '@/lib/projectEntityCache';
import {
  archivedProjectIdsPredicate,
  captureProjectListRollback,
  invalidateArchivedProjectCaches,
  invalidateReadyMonthProjectLists,
  mergeSingleProjectInCaches,
  patchActiveTableListCaches,
  syncArchivedCachesAfterProjectUpdated,
  syncProjectCollectionsSecondaryCaches,
  syncReadyMonthCachesAfterProjectUpdated,
  type ProjectListRollbackContext,
} from '@/lib/projectListQueryCache';
import { invalidateSummariesForProjectIds } from '@/lib/summariesBatchReload';

/**
 * «Убрать в архив» / «Вернуть из архива» до ответа сервера — общий шаг таблицы проектов и меню нового дизайна:
 * сущность получает is_archived, проект уходит из активных (или возвращается в них), готовые по месяцам и архив сверяются.
 * Возвращает снимок списков для отката (rollbackProjectsAndReconcile).
 */
export async function applyProjectArchiveOptimistic(
  queryClient: QueryClient,
  projectId: string,
  isArchived: boolean,
  fallback?: Project
): Promise<ProjectListRollbackContext> {
  const rollback = captureProjectListRollback(queryClient);
  markProjectUpdatedByMutation(projectId);
  await queryClient.cancelQueries({ queryKey: PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY });
  await queryClient.cancelQueries({ predicate: archivedProjectIdsPredicate });
  await queryClient.cancelQueries({ queryKey: ARCHIVED_COUNT_QUERY_KEY });

  const prevProject = getProjectEntityFromCaches(queryClient, projectId) ?? fallback;
  if (!prevProject) return rollback;
  const optimistic: Project = { ...prevProject, is_archived: isArchived };
  queryClient.setQueryData<Project>(projectEntityQueryKey(projectId), optimistic);
  setProjectEntity(queryClient, optimistic);

  patchActiveTableListCaches(queryClient, (activePrev) => {
    if (!activePrev) return activePrev;
    const excluded = isProjectExcludedFromActiveTable(optimistic.state) || !!optimistic.is_archived;
    const idx = activePrev.findIndex((p) => p.id === projectId);
    if (excluded) {
      if (idx < 0) return activePrev;
      return activePrev.filter((p) => p.id !== projectId);
    }
    const sessions = optimistic.sessions ?? (idx >= 0 ? activePrev[idx].sessions : undefined) ?? [];
    const merged = { ...optimistic, sessions };
    if (idx >= 0) return activePrev.map((p) => (p.id === projectId ? merged : p));
    return [merged, ...activePrev];
  });

  const syncRm = syncReadyMonthCachesAfterProjectUpdated(queryClient, optimistic);
  if (syncRm.invalidateSummaries) {
    void queryClient.invalidateQueries({ queryKey: ['ready-month-summaries'] });
  }
  invalidateReadyMonthProjectLists(queryClient, syncRm.readyMonthLists);

  const archSync = syncArchivedCachesAfterProjectUpdated(queryClient, optimistic);
  invalidateArchivedProjectCaches(queryClient, { list: archSync.archivedList, count: archSync.invalidateArchivedCount });

  syncProjectCollectionsSecondaryCaches(queryClient);
  return rollback;
}

/** Ответ сервера на архивирование — во все кэши списков, архива и сводок */
export function applyProjectArchiveSaved(queryClient: QueryClient, fresh: Project): void {
  mergeSingleProjectInCaches(queryClient, fresh);
  const archSync = syncArchivedCachesAfterProjectUpdated(queryClient, fresh);
  invalidateArchivedProjectCaches(queryClient, { list: archSync.archivedList, count: archSync.invalidateArchivedCount });
  invalidateSummariesForProjectIds(queryClient, [fresh.id]);
}
